//#region Imports
import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { version } from 'environments/version';
import { filter } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import {
  AdministatorPath,
  AuthenticationPath,
  DashboardPath,
} from 'app/core/common/route-constant';

import { AppDataService } from 'app/modules/authentication/services/appdata.service';
import { Constant } from '../../common/constant';
import { Dropdown } from '../../model/common/dropdown.model';
import { LoggedUser } from '../../model/users/logged-user.modal';
import { CommonService } from '../../services/common.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { RouteHistoryService } from '../../services/route-history.service';
import { PagingConfig } from 'app/core/feature/rest/pagingConfig';

//#endregion Imports

@Component({
  selector: 'app-default-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
})
export class DefaultLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  //#region Timeout
  sessionTimeout = 600; // 600 seconds = 10 minutes
  timedOut: boolean = false;
  session_expired: boolean = false;
  idleState: string = 'Active';
  private readonly NAVBAR_HEIGHT = '65px';
  private readonly SIDEBAR_WIDTH = '260px';
  private subs: Subscription[] = [];
  private lastActivityWriteTime = 0;
  private readonly ACTIVITY_THROTTLE = 5000;
  private activityHandler!: () => void;
  private storageHandler!: (event: StorageEvent) => void;
  private beforeUnloadHandler!: () => void;
  private resizeHandler!: () => void;
  remainingTime: any;
  //#endregion

  //#region Variable
  sideBarStatus: boolean = true;
  @ViewChild('viewportChecker', { static: true })
  viewportCheckerRef: ElementRef;
  viewHeight: number;
  loggedInUser: LoggedUser = null;
  appClass: string;
  rightBar = false;
  rightBarRole = Constant.RightBar;
  menuRoleName: string;
  currentRole: string;
  showKYC: boolean = false;
  notification: any = [];
  notificationCount: number = 0;
  no_of_day: number = 0;
  plan_expiring: boolean = false;
  displayPlan: boolean = false;
  version_update: boolean = false;
  expired: boolean = false;
  renewal_mode: boolean = false;
  displayReminder: boolean = false;
  showKYCNotice: boolean = false;
  KYCNoticeType: number = 0;
  reminder_type: string = '';
  set_pin: boolean = false;
  classification_list: any[] = [];
  show_classification: boolean = false;
  leftTooltipItems: MenuItem[] = [
    {
      tooltipOptions: {
        tooltipLabel: 'Video Tutorials',
        tooltipPosition: 'left',
      },
      icon: 'icon-fab-video',
      command: () => {
        this.router.navigate([
          `${
            AdministatorPath.Administrator +
            '/' +
            AdministatorPath.VideoTutorial
          }`,
        ]);
      },
    },
    {
      tooltipOptions: {
        tooltipLabel: 'FAQs',
        tooltipPosition: 'left',
      },
      icon: 'icon-fab-faq',
      command: () => {
        this.router.navigate([
          `${AdministatorPath.Administrator + '/' + AdministatorPath.FAQ}`,
        ]);
      },
    },
    {
      icon: 'icon-fab-help',
      tooltipOptions: {
        tooltipLabel: 'Help',
        tooltipPosition: 'top',
      },
      command: () => {
        this.sendHelptext();
      },
    },
  ];
  currentChangedRole: string = '';
  rbi_notice: boolean = true;
  pagingConfig: PagingConfig = new PagingConfig();
  unreadNotificationCount: number = 0;
  current_app: string;
  visible: boolean;
  custom_style: any = {};
  //#endregion

  //#region Constructor
  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private appData: AppDataService,
    private commonService: CommonService,
    private spinner: NgxSpinnerService,
    private routeHistoryService: RouteHistoryService,
    private idle: Idle
  ) {
    this.current_app =
      this.appData.currentApp == null ? 'EMS' : this.appData.currentApp;
  }
  //#endregion Constructor

  ngOnInit() {
    //#region idle case
    // 1️⃣ If app was closed for more than 10 minutes, log out immediately
    const lastActive = localStorage.getItem('lastActive');
    if (lastActive && Date.now() - parseInt(lastActive, 10) > this.sessionTimeout * 1000) {
      this.appData.logout();
      this.router.navigate([`${AuthenticationPath.Login}`]);
      return;
    }
    localStorage.removeItem('lastActive');

    // 2️⃣ Start Idle Monitoring
    this.idle.setIdle(600);
    this.idle.setTimeout(30); // 30-second warning window before forced logout
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.subs.push(
      this.idle.onIdleStart.subscribe(() => {
        this.idleState = 'You have gone idle!';
      }),
      this.idle.onTimeout.subscribe(() => {
        this.idleState = 'Session timed out!';
        this.timedOut = true;
        this.session_expired = true;
        this.appData.logout();
        this.router.navigate([`${AuthenticationPath.Login}`]);
      }),
      this.idle.onIdleEnd.subscribe(() => {
        this.idleState = 'Active';
      })
    );

    this.idle.watch();

    // 3️⃣ Listen for activity (cross-tab sync) and lifecycle events
    this.addActivityListeners();
    //#endregion

    this.rbi_notice = localStorage.getItem('rbi_notice') === null;
    // Check for version updates
    this.verify_version();

    // Set user roles locally
    this.setRolesLocal();

    // Store user data
    this.loggedInUser = this.appData.connectedUser;
    this.currentRole = this.appData.currentUserRole;

    // Subscribe to renewal mode (previously in constructor)
    this.subs.push(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          if (
            event.url.includes('/administrator/manage-subscription') ||
            event.url.includes('/administrator/upgrade-subscription')
          ) {
            this.renewal_mode = true;
            this.displayReminder = false;
          } else {
            this.renewal_mode = false;
          }
        })
    );

    // Subscribe to role changes (update currentRole)
    this.subs.push(
      this.appData.role$.subscribe((role) => {
        this.currentRole = role.role_name;
      })
    );

    // Calculate remaining days until plan expiration (if applicable)
    this.calculatePlanExpiration();

    // Detect changes for UI updates
    this.cdRef.detectChanges();

    // Listen for loading events
    this.listenToLoading();

    // Load notifications
    this.loadNotification({ getAll: 0 });

    // Update navigation history
    this.subs.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.routeHistoryService.setLastRoute(event.urlAfterRedirects);
        }
      })
    );

    // Subscribe to purchase plan event
    this.subs.push(
      this.commonService.purchasePlanEmitter.subscribe((purchase) => {
        if (purchase) {
          this.toggelPlan();
        }
      })
    );

    // Calculate reminders (optional, based on your needs)
    this.calculateReminder();
    // Get User Permission
    this.getUserPermission();
    localStorage.removeItem('sso');
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
    this.idle.stop();
    ['mousemove', 'keydown', 'click'].forEach(event => {
      window.removeEventListener(event, this.activityHandler);
    });
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('storage', this.storageHandler);
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  // Helper function to calculate plan expiration details
  calculatePlanExpiration() {
    if (!this.loggedInUser?.organization?.end_date) {
      return; // No end date, skip calculations
    }

    const targetDate = new Date(this.loggedInUser.organization.end_date);
    const currentDate = new Date();
    const timeDiff = targetDate.getTime() - currentDate.getTime();

    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    this.no_of_day = daysRemaining;
    this.plan_expiring = daysRemaining <= 25;
    this.expired = daysRemaining < 0;
    this.appData.plan_expired = this.expired;
  }

  ngAfterViewInit() {
    this.loggedInUser = this.appData.connectedUser;
    this.handleKyc();
    this.cdRef.detectChanges();
    this.checkForClassificationExpense();
    this.checkViewportHeight();
    this.resizeHandler = this.checkViewportHeight.bind(this);
    window.addEventListener('resize', this.resizeHandler);
    setTimeout(() => {
      this.closeNotice();
    }, 50000);
  }

  private getViewportHeight(): number {
    return Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );
  }

  private checkViewportHeight(): void {
    this.viewHeight = this.getViewportHeight();
    this.cdRef.detectChanges();
  }

  sideBarEventHandler(event: boolean) {
    if (event) {
      this.appClass =
        'vertical-layout vertical-menu-modern navbar-sticky footer-static pace-done menu-collapsed';
      this.sideBarStatus = false;
    } else {
      this.appClass =
        'vertical-layout vertical-menu-modern content-left-sidebar footer-static pace-done menu-expanded navbar-sticky';
    }
    this.cdRef.detectChanges();
  }

  onLogout() {
    this.appData.navigateUrl = null;
    this.appData.logout(); // Clears localStorage and memory data, broadcasts logout event
    this.router.navigate([`${AuthenticationPath.Login}`]);
  }

  rightBarHandler(type: string) {
    this.rightBar = true;
    if (this.rightBarRole.Notification == type) {
      this.menuRoleName = this.rightBarRole.Notification;
    } else if (this.rightBarRole.Profile == type) {
      this.menuRoleName = this.rightBarRole.Profile;
    } else if (this.rightBarRole.Setting == type) {
      this.menuRoleName = this.rightBarRole.Setting;
    }
  }

  setRolesLocal() {
    this.commonService.getRoleForUser().subscribe((x) => {
      const roleList = x as unknown as Dropdown[];
      let roleId = roleList.findIndex(
        (x) => x.name.toLocaleLowerCase() == Constant.RoleName.Admin
      );
      localStorage.setItem(
        Constant.RoleName.Admin.toLocaleLowerCase(),
        roleList[roleId].id.toString()
      );

      roleId = roleList.findIndex(
        (x) => x.name.toLocaleLowerCase() == Constant.RoleName.Finance
      );
      localStorage.setItem(
        Constant.RoleName.Finance.toLocaleLowerCase(),
        roleList[roleId].id.toString()
      );

      roleId = roleList.findIndex(
        (x) => x.name.toLocaleLowerCase() == Constant.RoleName.Manager
      );
      localStorage.setItem(
        Constant.RoleName.Manager.toLocaleLowerCase(),
        roleList[roleId].id.toString()
      );

      roleId = roleList.findIndex(
        (x) => x.name.toLocaleLowerCase() == Constant.RoleName.User
      );
      localStorage.setItem(
        Constant.RoleName.User.toLocaleLowerCase(),
        roleList[roleId].id.toString()
      );
    });
  }

  handleKyc() {
    let status = this.appData.connectedUser.kyc_status;
    if (
      status == Constant.KYCStatus.Initiated ||
      status == Constant.KYCStatus.Pending
    ) {
      this.showKYC = true;
    } else if (status == Constant.KYCStatus.Completed) {
      this.showKYC = false;
    }
  }

  handleSideBarEvent(_event: boolean) {
    this.rightBar = !this.rightBar;
  }

  handleUserSetting(_$event: boolean) {
    this.rightBar = true;
    this.menuRoleName = this.rightBarRole.Setting;
  }

  listenToLoading(): void {
    this.subs.push(
      this.commonService.data$.subscribe((loading) => {
        if (loading == 'set-pin') {
          let user = this.appData.connectedUser;
          user.pin_status = false;
          this.appData.connectedUser = user;
          this.set_pin = true;
        }
        if (loading == 'show') {
          this.spinner.show();
        } else {
          this.spinner.hide();
        }
      })
    );
  }

  loadNotification(data: { getAll?: number } = {}) {
    if (data?.getAll == 1 && this.unreadNotificationCount > 0) {
      this.notification = [];
      this.pagingConfig = new PagingConfig();
    }

    let payload = { ...data, pagination: this.pagingConfig };

    this.commonService.getUserNotification(payload).subscribe((x) => {
      if (this.pagingConfig?.pageNumber === 1) {
        this.notification = [];
      }
      if (x && x.notifications?.results) {
        this.notification = this.notification.concat(x.notifications.results);
        this.notificationCount = x.total_notifications;
        this.unreadNotificationCount = x.unread_count;

        this.pagingConfig.pageNumber++;
        this.pagingConfig.count = x.notifications.count;
        this.pagingConfig.next =
          this.notification.length < this.pagingConfig.count;
      } else {
        this.notification = [];
      }
    });
  }

  handleMarkNotification() {
    this.notificationCount = 0;
    this.unreadNotificationCount = 0;
  }

  toggelPlan() {
    if (this.loggedInUser.organization.subscription_type_id == 4) {
      this.router.navigate([
        `${
          AdministatorPath.Administrator +
          '/' +
          AdministatorPath.ManageSubscription
        }`,
      ]);
    } else {
      this.displayPlan = !this.displayPlan;
    }
  }

  toggleSideMenu() {
    this.sideBarEventHandler(false);
    this.sideBarStatus = true;
  }

  verify_version() {
    const queryParams = {
      platform: 'WEB',
      version: version,
    };
    let str = `/compare_version/`;
    this.commonService.verify_version(str, queryParams).subscribe((x) => {
      this.version_update = !x.status;
    });
  }

  reloadApp() {
    window.location.reload();
  }
  handleSubcriptionEvent(_event: boolean) {
    this.displayPlan = !this.displayPlan;
  }

  calculateReminder() {
    if (
      this.currentRole.toLocaleLowerCase() ===
        Constant.RoleName.Admin.toLocaleLowerCase() ||
      this.currentRole.toLocaleLowerCase() ==
        Constant.RoleName.Finance.toLocaleLowerCase()
    ) {
      const isPaid = this.loggedInUser.organization.subscription_type_id;
      const reminders = {
        5: isPaid === 1 ? '5' : '5P',
        15: isPaid === 1 ? '15' : '15P',
        25: isPaid === 1 ? '25' : null,
      };
      if (
        reminders.hasOwnProperty(this.no_of_day) &&
        this.renewal_mode == false
      ) {
        this.reminder_type = reminders[this.no_of_day];
        if (this.reminder_type !== null) {
          this.displayReminder = true;
        }
      }
    }
  }

  checkUserKycStatus() {
    this.commonService.getCardDetails().subscribe((data) => {
      this.KYCNoticeType = data.status;
      if (data.status == 1) {
        this.loggedInUser.kyc_status = 'C';
        this.appData.connectedUser = this.loggedInUser;
        this.showKYC = !this.showKYC;
        this.showKYCNotice = true;
        setTimeout(() => {
          this.showKYCNotice = false;
        }, 3000);
      } else {
        this.showKYCNotice = true;
        setTimeout(() => {
          this.showKYCNotice = false;
        }, 10000);
      }
    });
  }

  handleUpgradeType(event: string) {
    this.displayReminder = false;

    if (event == 'FREE') {
      this.displayPlan = !this.displayPlan;
    } else {
      this.router.navigate([
        `${
          AdministatorPath.Administrator +
          '/' +
          AdministatorPath.ManageSubscription
        }`,
      ]);
    }
  }

  closeReminder() {
    this.displayReminder = false;
  }

  handlePinStatus(_event: string) {
    this.set_pin = !this.set_pin;
  }

  toggleMaskData(_value: number) {}

  sendHelptext() {
    let msg =
      'Hi, I need assistance regarding BizPay. My user name is ' +
      this.appData.connectedUser.first_name +
      ' ' +
      this.appData.connectedUser.last_name +
      ', and Email id is *' +
      this.appData.connectedUser.email +
      '*';
    let whatsappUrl = 'https://wa.me/+917021882996?text=' + msg;
    window.open(whatsappUrl, '_blank');
  }

  checkForClassificationExpense() {
    this.commonService.checkForClassificationExpense().subscribe((resp) => {
      if (resp.length != 0) {
        this.classification_list = resp;
        this.show_classification = true;
      } else {
        this.show_classification = false;
      }
    });
  }

  handleClassifyModal(event: boolean) {
    this.show_classification = event;
  }

  handleRoleChange(changedRole: string) {
    this.currentChangedRole = changedRole;
  }

  addActivityListeners() {
    // Throttled handler: writes to localStorage for cross-tab sync only
    // ng-idle's DEFAULT_INTERRUPTSOURCES already resets the idle timer in the current tab
    this.activityHandler = () => {
      const now = Date.now();
      if (now - this.lastActivityWriteTime > this.ACTIVITY_THROTTLE) {
        this.lastActivityWriteTime = now;
        localStorage.setItem('lastActivity', now.toString());
      }
    };

    // Single consolidated storage handler for cross-tab sync
    this.storageHandler = (event: StorageEvent) => {
      if (event.key === 'lastActivity') {
        this.resetTimer();
      }
    };

    // Write lastActive timestamp so the next session can detect a long absence
    this.beforeUnloadHandler = () => {
      localStorage.setItem('lastActive', Date.now().toString());
    };

    // Register all listeners — without these the handlers above are never called
    ['mousemove', 'keydown', 'click'].forEach(event => {
      window.addEventListener(event, this.activityHandler);
    });
    window.addEventListener('storage', this.storageHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  resetTimer() {
    this.remainingTime = this.sessionTimeout; // Reset the countdown timer
    this.idle.watch(); // Reset idle monitoring
  }



  closeNotice(){
    this.rbi_notice=false;
    localStorage.setItem('rbi_notice','0')
  }

  getUserPermission() {
    this.commonService
      .getUserPermissionById(this.appData.connectedUser.user_id)
      .subscribe((res) => {
        this.appData.userPermission = res;
      });
  }

  setApp(mode: string) {
    this.appData.currentApp = mode;
    this.current_app = mode;
    this.visible = false;
    mode == 'VMS' 
    ? this.router.navigateByUrl(`${DashboardPath.Dashboard+ '/' +DashboardPath.VendorDashboard}`) 
    : this.router.navigateByUrl(`${DashboardPath.Dashboard+ '/' +DashboardPath.AdminDashboard}`);
  }

  handleChangeApp(_appData: boolean) {
    this.showDialog('top-left');
  }

  showDialog(position: string) {
    if (position == 'top-right') {
      this.custom_style = {
        position: 'absolute',
        top: this.NAVBAR_HEIGHT,
        right: '0',
        width: '25rem',
      };
    } else {
      this.custom_style = {
        position: 'absolute',
        top: this.NAVBAR_HEIGHT,
        left: this.SIDEBAR_WIDTH,
        width: '25rem',
      };
    }
    this.visible = true;
  }
}
